import { supabase } from './supabase';
import { ApiKey } from './types';
import { SecurityService } from './securityService';

export class ApiKeyService {
  
  /**
   * Récupère la configuration complète d'une API par son service
   */
  async getApiConfig(service: string): Promise<ApiKey | null> {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('service', service)
        .single();

      if (error) {
        console.error(`Erreur lors de la récupération de la configuration API pour ${service}:`, error);
        return null;
      }

      return data || null;
    } catch (error) {
      console.error(`Exception lors de la récupération de la configuration API pour ${service}:`, error);
      return null;
    }
  }
  
  /**
   * Récupère une métadonnée spécifique pour un service
   */
  async getMetadata<T>(service: string, key: string): Promise<T | null> {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('metadata')
        .eq('service', service)
        .single();

      if (error) {
        console.error(`Erreur lors de la récupération des métadonnées pour ${service}:`, error);
        return null;
      }

      return data?.metadata?.[key] || null;
    } catch (error) {
      console.error(`Exception lors de la récupération des métadonnées pour ${service}:`, error);
      return null;
    }
  }
  
  /**
   * Récupère toutes les clés API disponibles
   */
  async getAllApiKeys(): Promise<ApiKey[]> {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*');

      if (error) {
        console.error('Erreur lors de la récupération de toutes les clés API:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Exception lors de la récupération de toutes les clés API:', error);
      return [];
    }
  }

  /**
   * Enregistre une clé API pour un service (avec chiffrement)
   */
  async saveApiKey(service: string, apiKey: string, metadata?: Record<string, any>): Promise<boolean> {
    try {
      // Valide et nettoie la clé API
      const sanitizedKey = SecurityService.sanitizeApiKey(apiKey);
      
      // Chiffre la clé API avant de la stocker
      const encryptedKey = await SecurityService.encryptApiKey(sanitizedKey);
      
      const data: Record<string, any> = { 
        service, 
        api_key: encryptedKey, 
        updated_at: new Date().toISOString() 
      };
      
      // Si c'est un service OpenAI, extraire les paramètres spécifiques dans des colonnes dédiées
      if (service === 'openai' && metadata) {
        // Extraire les paramètres dans des colonnes dédiées
        if (metadata.model) {
          data.model = metadata.model;
        }
        if (metadata.temperature) {
          data.temperature = metadata.temperature.toString();
        }
        if (metadata.max_tokens) {
          data.max_tokens = metadata.max_tokens.toString();
        }
        
        // Conserver également les métadonnées complètes
        data.metadata = metadata;
      } else if (metadata) {
        // Pour les autres services, conserver uniquement les métadonnées
        data.metadata = metadata;
      }
      
      
      const { error } = await supabase
        .from('api_keys')
        .upsert(data, { onConflict: 'service' });

      if (error) {
        console.error(`Erreur lors de l'enregistrement de la clé API pour ${service}:`, error);
        return false;
      }

      return true;
    } catch (error) {
      console.error(`Exception lors de l'enregistrement de la clé API pour ${service}:`, error);
      return false;
    }
  }
  
  /**
   * Met à jour uniquement les métadonnées d'une API
   */
  async updateMetadata(service: string, metadata: Record<string, any>): Promise<boolean> {
    try {
      // Récupérer d'abord les métadonnées existantes
      const { data: existingData, error: fetchError } = await supabase
        .from('api_keys')
        .select('metadata')
        .eq('service', service)
        .single();
      
      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = not found
        console.error(`Erreur lors de la récupération des métadonnées pour ${service}:`, fetchError);
        return false;
      }
      
      // Fusionner avec les métadonnées existantes si présentes
      const updatedMetadata = {
        ...(existingData?.metadata || {}),
        ...metadata
      };
      
      const { error } = await supabase
        .from('api_keys')
        .update({ 
          metadata: updatedMetadata,
          updated_at: new Date().toISOString() 
        })
        .eq('service', service);

      if (error) {
        console.error(`Erreur lors de la mise à jour des métadonnées pour ${service}:`, error);
        return false;
      }

      return true;
    } catch (error) {
      console.error(`Exception lors de la mise à jour des métadonnées pour ${service}:`, error);
      return false;
    }
  }

  /**
   * Récupère une clé API par son service (avec déchiffrement)
   */
  async getApiKey(service: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('api_key')
        .eq('service', service)
        .single();

      if (error) {
        console.error(`Erreur lors de la récupération de la clé API pour ${service}:`, error);
        return null;
      }

      if (!data?.api_key) {
        return null;
      }

      // Tente de déchiffrer la clé API
      try {
        const decryptedKey = await SecurityService.decryptApiKey(data.api_key);
        return decryptedKey;
      } catch (decryptError) {
        console.warn(`La clé API pour ${service} semble être en plain text, migration nécessaire`);
        // Si le déchiffrement échoue, c'est probablement une clé en plain text
        // On la retourne telle quelle pour compatibilité arrière
        return data.api_key;
      }
    } catch (error) {
      console.error(`Exception lors de la récupération de la clé API pour ${service}:`, error);
      return null;
    }
  }

  /**
   * Supprime une clé API pour un service
   */
  async deleteApiKey(service: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('service', service);

      if (error) {
        console.error(`Erreur lors de la suppression de la clé API pour ${service}:`, error);
        return false;
      }

      return true;
    } catch (error) {
      console.error(`Exception lors de la suppression de la clé API pour ${service}:`, error);
      return false;
    }
  }
}

export const apiKeyService = new ApiKeyService();



// Utilitaire pour la migration depuis d'autres tables - gère maintenant l'absence des tables
export async function migrateApiKeysFromOtherTables(): Promise<void> {
  try {
    
    // Vérifier les clés existantes
    const existingKeys = await apiKeyService.getAllApiKeys();
    
    // Comme les anciennes tables ont été supprimées, nous n'avons plus besoin de faire de migration
    // Cette fonction est conservée pour compatibilité arrière mais n'effectue plus de migration
  } catch (error) {
    console.error('Erreur lors de la vérification des clés API:', error);
  }
}
